import { Entity } from '@core/entities/entity'
import { plainToInstance, Type } from 'class-transformer'
import { TemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import {
  ChecklistInspectionStats,
  ChecklistPermittedActions,
  ChecklistStatus,
} from '@core/entities/checklist/Checklist/types'
import { IChecklist } from '@core/entities/checklist/Checklist/IChecklist'
import { localizedStrings } from '@core/strings'
import { TemplateProjectLocations } from '@core/entities/template/TemplateProjectLocations'
import { ChecklistTemplate } from '@core/entities/checklist/ChecklistTemplate'
import { User } from '@core/entities/user'
import { Participant } from '@core/entities/participant/Participant'
import { OwnedByRegisterItem } from '@core/entities/register/OwnedByRegisterItem/OwnedByRegisterItem'
import { Feature, GeoJSON, Point } from 'geojson'
import { Intent, SelfBriefingChecklistIntent } from '@core/entities/intent'
import { Comments } from '@core/entities/comments'
import { ChecklistRegisterInspection } from '@core/entities/checklist/ChecklistRegisterInspection'
import { BriefingSession } from '@core/entities/briefing'
import { ImageWarning } from '@core/entities/ImageWarning'
import { ChecklistApprovalReviews } from '@core/entities/checklist/ChecklistApprovalReviews'
import { ChecklistApprovalWrapper } from '@core/entities/checklist/ChecklistApprovalWrapper'
import { ChangeRequest } from '@core/entities/permit/ChangeRequest'
import { statuses } from '@core/theme/statuses'

export class Checklist extends Entity<IChecklist> {
  id: number

  permitId?: number

  organisationId: number

  project: {
    id: number
    name: string
    timezone: string
  }

  uuid: string

  shortUUID: string

  createdOn: string

  modifiedOn: string

  @Type(() => ChecklistTemplate)
  template: ChecklistTemplate

  status: ChecklistStatus

  @Type(() => User)
  createdBy: User

  @Type(() => User)
  modifiedBy: User

  locationId: string

  locationName: string

  summary: string

  draftName?: string

  selfAccess?: { token: string; validUntil: string }

  outcome?: 'PASS' | 'FAIL'

  stats?: ChecklistInspectionStats

  isInspection?: boolean

  @Type(() => TemplateFormAnswers)
  mainAnswers: TemplateFormAnswers

  @Type(() => Participant)
  participants: Array<Participant>

  @Type(() => BriefingSession)
  lastBriefingSession?: BriefingSession

  @Type(() => TemplateProjectLocations)
  projectLocations: TemplateProjectLocations

  geoJSON?: GeoJSON

  permittedActions: Array<ChecklistPermittedActions>

  @Type(() => OwnedByRegisterItem)
  ownedByRegisterItem: OwnedByRegisterItem

  comments: Comments

  commentsCount?: number

  @Type(() => ChecklistRegisterInspection)
  registerInspection?: ChecklistRegisterInspection

  @Type(() => ImageWarning)
  warnings: ImageWarning[]

  @Type(() => ChecklistApprovalReviews)
  approvalReviews?: ChecklistApprovalReviews

  @Type(() => ChangeRequest)
  lastRequestedChange?: ChangeRequest

  public static new(payload: unknown): Checklist {
    const entity = plainToInstance(Checklist, payload)

    return entity
  }

  public hasPermission(action: ChecklistPermittedActions): boolean {
    return this.permittedActions?.includes(action)
  }

  public getNextApproval(): ChecklistApprovalWrapper | undefined {
    return this.approvalReviews?.getNextApproval()
  }

  public isAwaitingChanges(): boolean {
    return (
      this.status === 'CHANGES_REQUESTED' &&
      !!this.lastRequestedChange?.requestedAt &&
      !this.lastRequestedChange.addressedBy
    )
  }

  public getApprovalProgressText() {
    const allApprovals = this.approvalReviews?.approvals
    const doneReviews = this.approvalReviews?.getReviews('APPROVED').length ?? 0
    const allReviews = allApprovals?.length

    if (
      this.status === 'AWAITING_APPROVAL' &&
      allReviews > 0 &&
      (!this.lastRequestedChange || this.lastRequestedChange?.addressedBy)
    ) {
      return localizedStrings.approvalProgressText(doneReviews, allReviews)
    }

    return false
  }

  public shouldShowNominateApprovers(): boolean {
    const suggestedApprovers =
      this.approvalReviews?.nominatedApprovers?.suggestedApprovers
    return !!suggestedApprovers?.length
  }

  public getStatusChipColor() {
    return Checklist.getStatusChipColor(this.status)
  }

  public static getStatusChipColor(status: ChecklistStatus) {
    switch (status) {
      case 'COMPLETED':
        return statuses.completed.color
      case 'APPROVED':
        return statuses.approved.color
      case 'AWAITING_APPROVAL':
        return statuses.approval.color
      case 'CHANGES_REQUESTED':
        return statuses.changesRequested.color
      case 'REJECTED':
        return statuses.rejected.color
      default:
        return 'success'
    }
  }

  public getStatusString(): string {
    return Checklist.getStatusString(this.status)
  }

  public getSecondaryStatusOptions():
    | Array<{ label: string; completed: boolean; user?: User }>
    | undefined {
    const approvals = this.approvalReviews?.approvals ?? []
    if (approvals.length <= 1) return undefined

    return approvals.map((approval) => {
      const approvalId = approval.approval?.id
      // For a pending stage, append the nominated approver names (mirrors permit).
      const labelPostfix = approvalId
        ? this.approvalReviews?.getNominatedApproverNames(approvalId)
        : ''

      return {
        label:
          approval.submittedBy?.getFullName() ||
          `${approval.approval?.label ?? ''}${
            labelPostfix ? ` - ${labelPostfix}` : ''
          }`,
        completed: approval.action === 'APPROVED',
        user: approval.submittedBy,
      }
    })
  }

  public getSecondaryStatusString(): string | undefined {
    if (this.status !== 'AWAITING_APPROVAL') {
      return undefined
    }

    if (this.lastRequestedChange) {
      return this.lastRequestedChange.isUnanswered()
        ? localizedStrings.changesRequested
        : localizedStrings.changesDone
    }

    if (this.approvalReviews?.hasNominatedApprovers()) {
      return this.approvalReviews.getNominatedApproverNames(
        this.approvalReviews.nextApprovalId,
      )
    }

    if ((this.approvalReviews?.approvals?.length ?? 0) > 1) {
      return this.approvalReviews?.getNextApproval()?.approval?.label
    }

    return undefined
  }

  public getCheckinLocation(): Feature<Point> {
    if (!(this.geoJSON && this.geoJSON.type === 'FeatureCollection')) return
    const checkinGeometry = this.geoJSON.features?.[0]
    if (!checkinGeometry) return
    const { geometry, ...feature } = checkinGeometry
    if (geometry.type !== 'Point') return
    return { geometry, ...feature }
  }

  public static getStatusString(status: ChecklistStatus): string {
    switch (status) {
      case 'COMPLETED':
        return statuses.completed.label
      case 'APPROVED':
        return statuses.approved.label
      case 'AWAITING_APPROVAL':
        return statuses.approval.label
      case 'CHANGES_REQUESTED':
        return statuses.changesRequested.label
      case 'REJECTED':
        return statuses.rejected.label
      default:
        return status
    }
  }

  public getBriefingIntentUrl(baseUrl: string): string {
    const intent: SelfBriefingChecklistIntent = {
      intentType: 'SELF_BRIEFING_CHECKLIST',
      checklistId: this.id,
      organisationId: this.organisationId,
      projectId: this.project.id,
      accessToken: this.selfAccess?.token,
      validUntil: this.selfAccess?.validUntil,
    }

    return `${baseUrl}/intent?data=${encodeURIComponent(
      JSON.stringify(intent),
    )}`
  }

  public getOpenIntentUrl(baseUrl: string): string {
    const intent: Intent = {
      intentType: this.isInspection ? 'OPEN_INSPECTION' : 'OPEN_CHECKLIST',
      organisationId: this.organisationId,
      projectId: this.project.id,
      checklistId: this.id,
    }
    return `${baseUrl}/intent?data=${encodeURIComponent(
      JSON.stringify(intent),
    )}`
  }

  public getImageS3Keys(): string[] {
    return this.mainAnswers.getImageS3Keys()
  }

  public getProofs() {
    return this.mainAnswers.getProofs()
  }

  public getTemplateRules() {
    return this.template.currentVersion?.getMainQuestions()?.toPlain()?.rules
  }

  public getActiveParticipants() {
    return (
      this.participants?.filter(
        (participant) => participant.status === 'ACTIVE',
      ) ?? []
    )
  }

  public hasOngoingBriefingSession() {
    return this.lastBriefingSession?.status === 'ONGOING'
  }

  public shouldShowBriefingCard() {
    return (
      this.hasOngoingBriefingSession() &&
      this.hasPermission('BRIEF_PARTICIPANTS')
    )
  }

  /**
   * Whether briefings on this entity must collect a participant signature.
   *
   * Checklists do not expose a "skip signature" template setting today, so this
   * is always `true`. The method is intentionally defined here (and not only on
   * `Permit`) so briefing flows shared between permits and checklists can call
   * `entity.requiresBriefingSignature()` polymorphically — keeping the
   * "no signature" branch a permit-only concern without sprinkling
   * `instanceof Permit` / entity-type checks across the UI layer.
   */
  public requiresBriefingSignature(): boolean {
    return true
  }

  /**
   * Checklists have no remote-acceptance template setting, so this is always
   * `false`. Defined for the same polymorphic reason as
   * `requiresBriefingSignature()`.
   */
  public requiresBriefingAcceptance(): boolean {
    return false
  }
}
